import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FileText, Clock, CheckCircle, AlertCircle,
  Loader2, Search, Play, Eye, Building2, ThumbsUp, XCircle
} from 'lucide-react';
import { getApiUrl } from '@/lib/api';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { label: 'Pending', color: 'bg-violet-100 text-violet-700', icon: Clock },
  ai_evaluated: { label: 'AI Evaluated', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  reviewed: { label: 'Reviewed', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  valuable: { label: 'Valuable', color: 'bg-emerald-100 text-emerald-700', icon: ThumbsUp },
  declined: { label: 'Declined', color: 'bg-violet-100 text-violet-700', icon: XCircle },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

interface PitchItem {
  id: string;
  product_name: string;
  category?: string;
  vendor_name?: string;
  status: string;
  overall_score: number | null;
  created_at: string;
}

export default function AdminPitchesPage() {
  const { token } = useAuth();
  const [pitches, setPitches] = useState<PitchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [evaluatingId, setEvaluatingId] = useState<string | null>(null);

  useEffect(() => {
    fetchPitches();
  }, []);

  const fetchPitches = async () => {
    try {
      const response = await fetch(getApiUrl('/api/pitches/admin/list?limit=100'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPitches(data);
      }
    } catch (err) {
      console.error('Failed to load pitches:', err);
    } finally {
      setLoading(false);
    }
  };

  const triggerEvaluation = async (pitchId: string) => {
    setEvaluatingId(pitchId);
    try {
      const response = await fetch(getApiUrl(`/api/evaluations/pitch/${pitchId}/evaluate`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchPitches();
      } else {
        const error = await response.json();
        alert(error.detail || 'Evaluation failed');
      }
    } catch (err) {
      alert('Failed to trigger evaluation');
    } finally {
      setEvaluatingId(null);
    }
  };

  const filteredPitches = pitches.filter(p => {
    const matchesSearch = p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.vendor_name && p.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()));

    if (filterStatus === 'all') return matchesSearch;
    return matchesSearch && p.status === filterStatus;
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-pitches-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-stone-900">All Pitches</h1>
          <p className="text-stone-600 mt-1">Manage and evaluate all pitches in the system</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search pitches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'ai_evaluated', 'reviewed', 'delivered'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-stone-900 text-white'
                    : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'
                }`}
              >
                {status === 'all' ? 'All' : STATUS_CONFIG[status]?.label || status}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-4 gap-4">
          {['pending', 'ai_evaluated', 'reviewed', 'delivered'].map((status) => {
            const count = pitches.filter(p => p.status === status).length;
            const config = STATUS_CONFIG[status];
            return (
              <div key={status} className="bg-white rounded-xl border border-stone-200 p-4">
                <p className="text-sm text-stone-500">{config.label}</p>
                <p className="text-2xl font-bold text-stone-900">{count}</p>
              </div>
            );
          })}
        </div>

        {/* Pitches Table */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-stone-400 mx-auto" />
            </div>
          ) : filteredPitches.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-600">No pitches found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Product</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Vendor</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Score</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Date</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-stone-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredPitches.map((pitch) => {
                    const statusConfig = STATUS_CONFIG[pitch.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    const isEvaluating = evaluatingId === pitch.id;

                    return (
                      <tr key={pitch.id} className="hover:bg-stone-50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-stone-900">{pitch.product_name}</p>
                            {pitch.category && (
                              <p className="text-xs text-stone-500">{pitch.category}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-stone-600">{pitch.vendor_name || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {pitch.overall_score !== null ? (
                            <span className="font-semibold text-stone-900">{pitch.overall_score}</span>
                          ) : (
                            <span className="text-stone-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-stone-500">
                          {formatDate(pitch.created_at)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {pitch.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => triggerEvaluation(pitch.id)}
                                disabled={!!isEvaluating}
                                className="text-violet-600 border-violet-200 hover:bg-violet-50"
                              >
                                {isEvaluating ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Play className="w-4 h-4 mr-1" />
                                    Evaluate
                                  </>
                                )}
                              </Button>
                            )}
                            <Link to={`/admin/pitches/${pitch.id}`}>
                              <Button size="sm" variant="ghost">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
